import * as React from 'react';
import type { Editor } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

export type ImageAlignment = 'left' | 'center' | 'right';
export interface ImagePropertyValues { alt: string; title: string; width: string; alignment: ImageAlignment }
export type ReviewedImageAttributes = { alt: string; title: string | null; width: number | null; height: number | null; textAlign: ImageAlignment | null };

/** Validates the panel values into image attributes; height follows the image's proportions. */
export function reviewedImageProperties(values: ImagePropertyValues, dimensions: { width: number; height: number }): ReviewedImageAttributes | string {
  const alt = values.alt.trim(), title = values.title.trim();
  if (!alt) return 'Describe the image so everyone can understand it.';
  if (alt.length > 500 || title.length > 500) return 'Use up to 500 characters for the description and title.';
  const width = values.width.trim() === '' ? null : Number(values.width);
  if (width !== null && (!Number.isSafeInteger(width) || width <= 0 || width > 4096)) return 'Enter a positive whole-number width up to 4096, or leave it blank for the original size.';
  if (width !== null && !(dimensions.width > 0 && dimensions.height > 0)) return 'Wait for the image to load before changing its size.';
  const height = width === null ? null : Math.max(1, Math.round(width * dimensions.height / dimensions.width));
  return { alt, title: title || null, width, height, textAlign: values.alignment === 'left' ? null : values.alignment };
}

interface Props {
  editor: Editor;
  node: ProseMirrorNode;
  getPos: () => number | undefined;
  dimensions: { width: number; height: number };
  onClose: () => void;
}

export function ImagePropertiesDrawer({ editor, node, getPos, dimensions, onClose }: Props) {
  // Hold the reviewed node, not whichever image happens to be selected when Apply is clicked.
  const [target] = React.useState(node);
  const id = React.useId();
  const [values, setValues] = React.useState<ImagePropertyValues>({
    alt: String(node.attrs.alt ?? ''), title: String(node.attrs.title ?? ''),
    width: node.attrs.width ? String(node.attrs.width) : (Math.round(dimensions.width) ? String(Math.round(dimensions.width)) : ''),
    alignment: node.attrs.textAlign === 'center' || node.attrs.textAlign === 'right' ? node.attrs.textAlign : 'left'
  });
  const [error, setError] = React.useState('');
  const readOnly = !editor.isEditable;
  React.useEffect(() => {
    const check = () => { const position = getPos(); if (editor.isDestroyed || typeof position !== 'number' || editor.state.doc.nodeAt(position) !== target) onClose(); };
    editor.on('transaction', check); editor.on('destroy', onClose);
    return () => { editor.off('transaction', check); editor.off('destroy', onClose); };
  }, [editor, getPos, onClose, target]);
  const apply = () => {
    const position = getPos();
    if (readOnly || editor.isDestroyed || typeof position !== 'number' || editor.state.doc.nodeAt(position) !== target) return;
    const reviewed = reviewedImageProperties(values, dimensions);
    if (typeof reviewed === 'string') { setError(reviewed); return; }
    const transaction = editor.state.tr.setNodeMarkup(position, undefined, { ...target.attrs, ...reviewed });
    const before = editor.state.doc;
    editor.view.dispatch(transaction);
    // Protected sections and other guards may refuse the change; report it instead of closing silently.
    if (editor.state.doc.eq(before) && !before.eq(transaction.doc)) { setError('The image could not be changed. Check the document’s editing restrictions.'); return; }
    onClose();
  };
  const set = (key: keyof ImagePropertyValues) => (event: React.ChangeEvent<HTMLInputElement>) => setValues(current => ({ ...current, [key]: event.target.value }));
  return <Sheet open onOpenChange={open => { if (!open) onClose(); }}>
    <SheetContent side="right" className="p-6 md:max-w-md" onCloseAutoFocus={event => { event.preventDefault(); if (!editor.isDestroyed) editor.view.focus(); }}>
      <SheetHeader className="p-0">
        <SheetTitle>Image properties</SheetTitle>
        <SheetDescription>Change this image’s description, size and alignment in the document.</SheetDescription>
      </SheetHeader>
      <form className="mt-6 space-y-4" onSubmit={event => { event.preventDefault(); apply(); }}>
        {readOnly && <p role="status" className="text-sm text-muted-foreground">This document is read only.</p>}
        <fieldset disabled={readOnly} className="space-y-4">
          <div className="space-y-2"><Label htmlFor={id + '-alt'}>Alternative text</Label><Input id={id + '-alt'} maxLength={500} value={values.alt} onChange={set('alt')} /></div>
          <div className="space-y-2"><Label htmlFor={id + '-title'}>Image title</Label><Input id={id + '-title'} maxLength={500} value={values.title} onChange={set('title')} /></div>
          <div className="space-y-2"><Label htmlFor={id + '-width'}>Width (pixels)</Label><Input id={id + '-width'} type="number" min="1" max="4096" step="1" value={values.width} onChange={set('width')} />
            <p className="text-sm text-muted-foreground">Height adjusts proportionally. Leave blank to use the original size.</p></div>
          <div className="space-y-2"><Label htmlFor={id + '-alignment'}>Image alignment</Label>
            <Select value={values.alignment} onValueChange={value => { if (value === 'left' || value === 'center' || value === 'right') setValues(current => ({ ...current, alignment: value })); }}>
              <SelectTrigger id={id + '-alignment'}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Centre</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
            </Select></div>
        </fieldset>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {!readOnly && <Button type="submit">Apply image properties</Button>}
      </form>
    </SheetContent>
  </Sheet>;
}
